export interface ChangeIntervalOverlayProps {
  /** Raw typed buffer, e.g. "1", "15", "4h". */
  buffer: string
  /** Human-readable description, e.g. "5 minutes". Empty while unparseable. */
  label: string
  /** Whether Enter would apply a supported interval. */
  valid: boolean
}

/**
 * Compact "Change interval" heads-up shown while the user is typing a quick
 * interval on a focused chart. A horizontal pill pinned near the top of the
 * chart — deliberately a light HUD, not a centered modal. Purely
 * presentational; the buffer + parsing live in `useIntervalQuickSwitch`.
 */
export function ChangeIntervalOverlay({ buffer, label, valid }: ChangeIntervalOverlayProps) {
  // The buffer already shows the number; keep only the unit word(s) on the right
  // so the digits aren't repeated (e.g. buffer "15" + "minutes", not "15 minutes").
  const unit = label.replace(/^\d+\s*/, '')
  const accentClass = valid
    ? 'text-primary border-primary/60'
    : 'text-destructive border-destructive/60'
  return (
    <div
      className={`pointer-events-none absolute left-1/2 top-3.5 z-30 flex -translate-x-1/2 items-center gap-3 whitespace-nowrap rounded-md border bg-[hsl(var(--chart-canvas)/0.92)] px-3.5 py-2 shadow-xl shadow-black/45 backdrop-blur-md ${accentClass}`}
    >
      <span className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--chart-text-muted))]">
        Interval
      </span>
      <span className="font-mono text-xl font-bold leading-none">{buffer}</span>
      <span className="h-[18px] w-px bg-foreground/15" />
      <span
        className={
          'text-xs font-medium ' + (valid ? 'text-[hsl(var(--chart-text))]' : 'text-destructive')
        }
      >
        {valid ? unit : unit ? `${unit} — unavailable` : 'Keep typing…'}
      </span>
    </div>
  )
}
